import Rx from 'rx';
import { getOS } from './AppInfo';
import * as config from '../config';
import breakpoints from './responsive_config';

/**
 * Convert dash-case to camelCase
 */
const toCamelCase = str => {
  if (typeof str !== 'string') {
    return str;
  }
  return str.replace(/-([a-z])/g, match => match[1].toUpperCase());
};

/**
 * Validate if the browser window width is above a specified breakpoint.
 * @param {Object} breakpoint - Object with a min and/or max value.
 * @return {boolean}
 */
export function isAbove(breakpointName) {
  const breakpoint = breakpoints[breakpointName];
  if (breakpoint.max && window.innerWidth > breakpoint.max) {
    return true;
  }
  return false;
}

/**
 * Validate if the browser window width is below a specified breakpoint.
 * @param {Object} breakpoint - Object with a min and/or max value.
 * @return {boolean}
 */
export function isBelow(breakpointName) {
  const breakpoint = breakpoints[breakpointName];
  if (breakpoint.min && window.innerWidth < breakpoint.min) {
    return true;
  }
  return false;
}

/**
 * Validate if the browser window width is within the responsive
 * breakpoint range.
 * @param {Object} breakpoint - Object with a min and/or max value.
 * @return {boolean}
 */
function widthMatchesBreakpoint(breakpoint) {
  const width = window.innerWidth;
  if (breakpoint.min && width < breakpoint.min) {
    return false;
  }
  if (breakpoint.max && width > breakpoint.max) {
    return false;
  }
  return true;
}

/**
 * A simple interface for determining the current responsive layout,
 * based on the same breakpoint widths that the CSS uses.
 *
 * NOTE: This should be used sparingly. In almost all cases
 * if you're using this instead of CSS, something is probably wrong.
 *
 * @example
 * ```
 *   import Responsive from 'modules/Responsive';
 *
 *   console.log(Responsive.layout.mobile);
 *
 *   // Be notified of when responsive values change
 *   Responsive.changeStream.subscribe(() => {
 *     console.log('Responsive breakpoints changed!');
 *   });
 * ```
 */
class ResponsiveModule {
  /**
   * What breakpoint widths does the window size currently match
   */
  is = {
    mobile: undefined,
    mobileNarrow: undefined,
    tablet: undefined,
    desktop: undefined,
    desktopNarrow: undefined,
    wide: undefined,
  };

  orderedBreakpoints = [
    'mobile',
    'tablet',
    'desktop',
  ];

  /**
   * The general layout the user is viewing: mobile or desktop.
   *
   * This is determined off the tablet responsive breakpoint.
   * Tablet width and below is mobile, else it's desktop.
   * Defaults to 'mobile'
   */
  layout = {
    mobile: true,
    desktop: false,
  };

  /**
   * Defines a property which matches the value on the angular
   * responsive service.
   *
   * @deprecated
   * Don't use this, I'm defining a property so that I can
   * raise a warning in the console.
   */
  // eslint-disable-next-line class-methods-use-this
  get iosApp() {
    console.warn('using responsiveService.iosApp is deprecated please use AppInfo.getOS() instead!');
    return getOS() === 'ios' && config.APP;
  }

  /**
   * Subscribe to this to get notified when the breakpoints change.
   *
   * @example
   * ```
   *   Responsive.changeStream.subscribe(() => {
   *     console.log('Responsive breakpoints changed!');
   *   });
   * ```
   */
  changeStream = new Rx.BehaviorSubject();

  constructor() {
    this.checkBreakpoints();
    window.addEventListener('resize', () => {
      this.checkBreakpoints();
    });
  }

  /**
   * Check the current browser width against the list of breakpoints.
   */
  checkBreakpoints() {
    const is = {};
    let didChange = false;

    // Check all breakpoints.
    Object.entries(breakpoints)
      .forEach(([name, range]) => {
        const key = toCamelCase(name);
        is[key] = widthMatchesBreakpoint(range);

        // APP is always mobile, mobile narrow, or tablet
        if (config.APP && key !== 'mobileNarrow' && key !== 'mobile' && key !== 'tablet') {
          is[key] = false;
        }

        if (is[key] !== this.is[key]) {
          didChange = true;
        }
      });

    // If APP doesn't match mobile, assume tablet
    if (config.APP && !is.mobile) {
      is.tablet = true;
      if (!this.is.tablet) {
        didChange = true;
      }
    }

    // Set layout
    this.layout.desktop = is.desktop;
    this.layout.mobile = !is.desktop;

    this.is = is;

    // Fire change stream
    if (didChange) {
      this.changeStream.onNext(this.is);
    }

    return this;
  }

  /**
   * Chainable Promise that ensures the DOM is loaded before calculating layout info
   * @param {Number} interval
   * @param {Number} timeout
   */
  async isReady(interval = 50, timeout = 500) {
    const endTime = Date.now() + timeout;
    const isDomReady = ((resolve, reject) => {
      if (document?.readyState === 'complete') {
        resolve(this);
      } else if (Date.now() < endTime) {
        setTimeout(() => isDomReady(resolve, reject), interval);
      } else {
        reject(new Error('timed out waiting for DOM to be ready'));
      }
    });

    return new Promise(isDomReady).then(instance => instance.checkBreakpoints());
  }
}

const Responsive = new ResponsiveModule();
export default Responsive;
